import { useTheme } from '@/hooks/useTheme';

interface ThemeToggleProps {
  readonly variant?: 'compact' | 'full';
}

const SunIcon = ({ size = 16, color }: { readonly size?: number; readonly color: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = ({ size = 16, color }: { readonly size?: number; readonly color: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const CompactToggle = ({ isDark, onToggle }: { readonly isDark: boolean; readonly onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className="focus-ring"
    aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    style={{
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 'var(--space-2)',
      color: 'var(--color-neutral-400)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 'var(--radius-md)',
      transition: 'color var(--duration-fast) var(--ease-out)',
      minHeight: '36px',
      minWidth: '36px',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-inverse)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-neutral-400)'; }}
  >
    {isDark ? (
      <SunIcon size={18} color="currentColor" />
    ) : (
      <MoonIcon size={18} color="currentColor" />
    )}
  </button>
);

const FullToggle = ({ isDark, onToggle }: { readonly isDark: boolean; readonly onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className="focus-ring"
    aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    style={{
      position: 'relative',
      width: '100%',
      height: '36px',
      borderRadius: 'var(--radius-lg)',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      backgroundColor: 'var(--color-neutral-800)',
      transition: 'background-color var(--duration-fast) var(--ease-out)',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
    }}
  >
    {/* Sliding highlight */}
    <span
      style={{
        position: 'absolute',
        top: '3px',
        left: isDark ? 'calc(50% + 1.5px)' : '3px',
        width: 'calc(50% - 4.5px)',
        height: '30px',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--color-neutral-700)',
        transition: 'left var(--duration-fast) var(--ease-out)',
      }}
    />

    {/* Light label */}
    <span
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        position: 'relative',
        zIndex: 1,
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-medium)',
        color: isDark ? 'var(--color-neutral-500)' : 'var(--text-inverse)',
        transition: 'color var(--duration-fast) var(--ease-out)',
      }}
    >
      <SunIcon color={isDark ? 'var(--color-neutral-500)' : 'var(--text-inverse)'} />
      Light
    </span>

    {/* Dark label */}
    <span
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        position: 'relative',
        zIndex: 1,
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-medium)',
        color: isDark ? 'var(--text-inverse)' : 'var(--color-neutral-500)',
        transition: 'color var(--duration-fast) var(--ease-out)',
      }}
    >
      <MoonIcon color={isDark ? 'var(--text-inverse)' : 'var(--color-neutral-500)'} />
      Dark
    </span>
  </button>
);

export const ThemeToggle = ({ variant = 'compact' }: ThemeToggleProps) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  if (variant === 'full') {
    return <FullToggle isDark={isDark} onToggle={toggleTheme} />;
  }

  return <CompactToggle isDark={isDark} onToggle={toggleTheme} />;
};
